/**
 * @fileoverview TourDetails.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useRef, useEffect, useState } from 'react'
import { useParams, Navigate, Link } from 'react-router-dom'
import TopImageBanner from '../components/tours/TopImageBanner'
import SEO from '../components/ui/SEO'
import { apiFetch } from '../utils/api'

const getIcon = (feature) => {
  switch (feature.toLowerCase()) {
    case 'transport':
    case 'car': 
    case 'van': return 'fi fi-br-car-side';
    case 'hotel': return 'fi fi-br-hotel';
    case 'meal': return 'fi fi-br-restaurant';
    case 'guide': return 'fi fi-br-users';
    case 'hiking': return 'fi fi-br-hiking';
    case 'bicycle': return 'fi fi-br-bicycle';
    case 'bed': return 'fi fi-br-bed';
    default: return 'fi fi-br-check';
  }
}

function TourDetails() {
  const { slug } = useParams();
  const itineraryRef = useRef(null);

  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [openDay, setOpenDay] = useState(0);
  const [showTierModal, setShowTierModal] = useState(false);

  
  // Prevent body scroll when modals are open
  useEffect(() => {
      const isAnyModalOpen = selectedExperience || showTierModal;
      
      if (isAnyModalOpen) {
          document.body.classList.add('overflow-hidden');
          document.documentElement.classList.add('overflow-hidden');
          if (window.__lenis) window.__lenis.stop();
      } else {
          document.body.classList.remove('overflow-hidden');
          document.documentElement.classList.remove('overflow-hidden');
          if (window.__lenis) window.__lenis.start();
      }
      
      return () => {
          document.body.classList.remove('overflow-hidden');
          document.documentElement.classList.remove('overflow-hidden');
          if (window.__lenis) window.__lenis.start();
      };
  }, [selectedExperience, showTierModal]);


  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);

    apiFetch(`/tours/${slug}`)
      .then(res => {
        if (res && res.data) {
          const t = res.data;
          const formatted = {
            id: t._id,
            name: t.title,
            price: t.price,
            premiumPrice: t.premiumPrice,
            duration: `${t.duration} Days | ${t.duration - 1} Nights`,
            features: t.featuredIcons && t.featuredIcons.length > 0 ? t.featuredIcons : (t.includes && t.includes.length > 0 ? t.includes : ['hotel', 'meal', 'car', 'guide']),
            longDescription: t.description || '',
            inclusions: t.includes && t.includes.length > 0 ? t.includes : ['Premium Hotel Stays (4-Star/5-Star)', 'English-speaking Chauffeur Guide', 'Private A/C Transport', 'Site Entry Tickets'],
            exclusions: t.excludes && t.excludes.length > 0 ? t.excludes : ['International flights', 'Visa fees', 'Lunch and dinners (unless specified)', 'Personal expenses & tips'],
            experiences: t.itinerary && t.itinerary.length > 0 ? t.itinerary.map(item => item.experience).filter(Boolean) : [],
            itinerary: t.itinerary && t.itinerary.length > 0 
              ? t.itinerary.map(day => ({ 
                  day: day.day, 
                  title: day.experience || `Day ${day.day}`, 
                  subtitle: day.subtitle || '',
                  description: day.description || '',
                  experiences: day.experiences || [],
                  activities: day.activities || []
                })) 
              : [],
            image: (t.image && typeof t.image === 'object' && (t.image.l || t.image.h)) 
              ? { l: t.image.l || t.image.h, h: t.image.h || t.image.l }
              : { l: (t.images && t.images[0]) || t.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800', h: (t.images && t.images[0]) || t.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800' }
          };
          setTour(formatted);
        } else {
          throw new Error('No data');
        }
      })
      .catch(() => {
        setTour(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className='flex flex-col w-full min-h-screen justify-center items-center pt-24 md:pt-32'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-KITH-primary'></div>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className='flex flex-col w-full min-h-screen justify-center items-center pt-24 md:pt-32 px-6 text-center'>
        <h2 className='text-3xl font-black text-gray-800 uppercase mb-4'>Tour Not Found</h2>
        <p className='text-gray-500 mb-8 max-w-md'>The tour plan you are looking for does not exist or has been removed.</p>
        <Link to="/tour-plans" className='px-8 py-4 bg-KITH-primary text-white font-bold rounded-2xl hover:bg-KITH-primary-dark transition-all shadow-md'>
          Back to Tour Plans
        </Link>
      </div>
    );
  }

  const tourSchema = tour ? {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    "name": tour.name,
    "description": tour.longDescription,
    "image": tour.image.h,
    "offers": {
      "@type": "Offer",
      "price": tour.price,
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "url": window.location.href
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": "124"
    },
    "itinerary": {
      "@type": "ItemList",
      "numberOfItems": tour.itinerary.length,
      "itemListElement": tour.itinerary.map((item, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "item": {
          "@type": "TouristAttraction",
          "name": `Day ${item.day}: ${item.title}`,
          "description": item.description
        }
      }))
    }
  } : null;

  const faqSchema = tour ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is included in the tour price?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The tour price typically includes premium accommodation, professional guiding services, private air-conditioned vehicle transport, and entrance fees for designated sites mentioned in the itinerary. Please refer to the specific inclusions list for detailed items."
        }
      },
      {
        "@type": "Question",
        "name": "Is transport provided throughout the tour?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, private air-conditioned transport is provided from your arrival at the airport until your departure, driven by a dedicated, tourist-board approved English-speaking chauffeur-guide."
        }
      },
      {
        "@type": "Question",
        "name": "What should I bring for the tour?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We recommend comfortable walking shoes, lightweight cotton clothing, sunscreen, sunglasses, insect repellent, a camera, and appropriate modest clothing (covering shoulders and knees) for visiting sacred temples."
        }
      },
      {
        "@type": "Question",
        "name": "When is the best time to visit Sri Lanka?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sri Lanka has two distinct monsoons, making it a year-round destination. The best time for the west and south coasts is from December to April, while the east coast is best from May to September."
        }
      }
    ]
  } : null;

  const scrollToItinerary = () => {
    itineraryRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className='flex flex-col w-full min-h-screen pb-20 text-left pt-24 md:pt-32'>
      <SEO 
        title={tour.name} 
        description={tour.longDescription} 
        keywords={`${tour.name}, Sri Lanka tour, visit Sri Lanka, travel agent Sri Lanka`}
        image={tour.image.h}
        schema={[tourSchema, faqSchema]}
      />
      {/* Hero Section */}
      <div className='px-[5%] md:px-[10%]'>
        <div className='relative w-full h-[50vh] md:h-[60vh]'>
            <TopImageBanner title={tour.name} hImage={tour.image.h} lImage={tour.image.l} />
        </div>
      </div>

      {/* Info Bar */}
      <div className='px-[5%] md:px-[10%] mt-8'>
        <div className='flex flex-wrap justify-between items-center bg-white p-6 md:p-8 rounded-2xl shadow-md border border-gray-100 gap-6'>
            <div className='flex flex-col'>
                <span className='text-[10px] md:text-xs text-gray-400 font-bold uppercase tracking-wider'>From</span>
                <span className='text-xl md:text-2xl font-bold text-gray-800'>USD {tour.price.toLocaleString()}.00/pp</span>
            </div>
            <div className='flex flex-col'>
                <span className='text-[10px] md:text-xs text-gray-400 font-bold uppercase tracking-wider'>Duration</span>
                <span className='text-xl md:text-2xl font-bold text-KITH-primary'>{tour.duration}</span>
            </div>
            <button 
                onClick={scrollToItinerary}
                className='px-8 py-3 md:px-10 md:py-4 bg-KITH-primary text-white text-sm md:text-base font-bold rounded-2xl hover:bg-KITH-primary-dark transition-all transform hover:scale-105 shadow-lg'
            >
                Itinerary
            </button>
        </div>
      </div>

      {/* Icon Bar */}
      <div className='px-[5%] md:px-[10%] mt-12 flex gap-8 md:gap-12 overflow-x-auto pb-4 scrollbarHide'>
        {tour.features && tour.features.map((feature, idx) => (
            <div key={idx} className='flex flex-col items-center gap-3 min-w-[80px]'>
                <div className='w-14 h-14 md:w-16 md:h-16 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 shadow-sm'>
                    <i className={`${getIcon(feature)} text-KITH-primary text-xl md:text-2xl`}></i>
                </div>
                <span className='text-[10px] font-bold text-gray-500 uppercase tracking-widest'>{feature}</span>
            </div>
        ))}
      </div>

      {/* Main Content Grid (Overview & Itinerary Left, Inclusions/Exclusions Right) */}
      <div className='px-[5%] md:px-[10%] mt-16 grid grid-cols-1 lg:grid-cols-12 gap-16'>
        
        {/* Left Column: Overview and Itinerary */}
        <div className='lg:col-span-8 flex flex-col gap-16'>
            
            {/* Overview Section */}
            <div>
                <h3 className='text-2xl md:text-3xl font-bold text-gray-800 mb-6'>Overview</h3>
                <div className='text-gray-600 leading-relaxed text-sm md:text-base text-justify whitespace-pre-line'>
                    {tour.longDescription}
                </div>
            </div>

            {/* Itinerary Section */}
            <div ref={itineraryRef} className='flex flex-col pt-16 border-t border-gray-100'>
                <h3 className='text-3xl md:text-4xl font-bold text-gray-800 mb-10'>Full Itinerary</h3>
                <div className='flex flex-col gap-4'>
                {tour.itinerary && tour.itinerary.map((day, idx) => (
                    <div key={idx} className='bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden'>
                        <button 
                            onClick={() => setOpenDay(openDay === idx ? null : idx)}
                            className='w-full px-6 py-4 md:px-8 md:py-5 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors text-left'
                        >
                            <div className='flex items-center gap-6'>
                                <div className='px-4 py-2 bg-KITH-primary/10 text-KITH-primary rounded-full flex items-center justify-center font-bold shrink-0 text-xs md:text-sm whitespace-nowrap uppercase tracking-wider'>
                                    Day {String(day.day).padStart(2, '0')}
                                </div>
                                <div className='flex flex-col'>
                                    <h4 className='text-lg md:text-xl font-bold text-gray-800 leading-tight'>{day.title}</h4>
                                    {day.subtitle && <span className='italic text-xs text-gray-500 font-medium mt-1'>{day.subtitle}</span>}
                                </div>
                            </div>
                            <span className={`w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs transition-transform duration-300 ${openDay === idx ? 'rotate-180 text-KITH-primary' : 'text-gray-500'}`}>
                                <i className='fi fi-br-angle-down mt-1'></i>
                            </span>
                        </button>
                        
                        {openDay === idx && (
                            <div className='px-6 md:px-8 pb-8 pt-6 border-t border-gray-50 animate-fadeIn'>
                                <p className='text-gray-600 leading-relaxed text-justify'>{day.description}</p>
                                
                                {/* Experience Thumbnails */}
                                {day.experiences && day.experiences.length > 0 ? (
                                    <div className='mt-8 flex flex-col gap-4'>
                                        <h5 className='text-sm font-bold text-gray-400 uppercase tracking-widest'>Included Experiences</h5>
                                        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
                                            {day.experiences.map((exp, i) => (
                                                <button 
                                                    key={i} 
                                                    onClick={() => setSelectedExperience(exp)}
                                                    className='flex flex-col p-3 bg-gray-50 border border-gray-100 rounded-2xl hover:bg-white hover:shadow-md hover:border-gray-200 transition-all text-left group'
                                                >
                                                    <div className='w-full h-32 rounded-xl overflow-hidden bg-gray-200 mb-3'>
                                                        <img src={(exp.image && typeof exp.image === 'object' ? (exp.image.h || exp.image.l) : exp.image) || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800'} alt={exp.title} className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-500' />
                                                    </div>
                                                    <span className='text-sm font-bold text-gray-800 group-hover:text-KITH-primary transition-colors line-clamp-1 px-1'>{exp.title}</span>
                                                    <span className='text-xs text-gray-500 line-clamp-2 mt-1 px-1'>{exp.description || 'View details'}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    day.activities && day.activities.length > 0 && (
                                        <div className='mt-8 flex flex-wrap gap-3'>
                                            {day.activities.map((act, i) => (
                                                <button 
                                                key={i} 
                                                onClick={() => setSelectedExperience({ title: act })}
                                                className='text-left px-5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 hover:bg-KITH-primary hover:text-white transition-all shadow-sm'
                                                >
                                                    <i className='fi fi-br-star text-xs mr-2 opacity-70'></i>
                                                    {act}
                                                </button>
                                            ))}
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                ))}
                </div>
            </div>
        </div>

        {/* Right Column: Inclusions and Exclusions */}
        <div className='lg:col-span-4 flex flex-col gap-10'>
            <div className='bg-white p-8 md:p-10 rounded-[2.5rem] border border-gray-100 shadow-sm sticky top-32'>
                <div className='mb-10'>
                    <h4 className='text-xl font-black text-gray-800 mb-6 uppercase tracking-wider flex items-center gap-3'>
                        <span className='w-8 h-8 rounded-full bg-green-100 text-green-500 flex items-center justify-center text-sm'>✓</span>
                        Inclusions
                    </h4>
                    <ul className='flex flex-col gap-4'>
                        {tour.inclusions && tour.inclusions.map((item, idx) => (
                            <li key={idx} className='flex gap-3 items-start text-sm md:text-base text-gray-600'>
                                <span className='text-green-500 font-bold'>•</span>
                                <span className='text-left'>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <h4 className='text-xl font-black text-gray-800 mb-6 uppercase tracking-wider flex items-center gap-3'>
                        <span className='w-8 h-8 rounded-full bg-red-100 text-red-500 flex items-center justify-center text-sm'>✗</span>
                        Exclusions
                    </h4>
                    <ul className='flex flex-col gap-4'>
                        {tour.exclusions && tour.exclusions.map((item, idx) => (
                            <li key={idx} className='flex gap-3 items-start text-sm md:text-base text-gray-600'>
                                <span className='text-red-500 font-bold'>•</span>
                                <span className='text-left'>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
      </div>

      {/* FAQ Accordion Section */}
      <div className='px-[5%] md:px-[10%] mt-24 pt-16 border-t border-gray-100'>
        <h3 className='text-3xl md:text-4xl font-bold text-gray-800 text-center mb-16'>Frequently Asked Questions</h3>
        <div className='max-w-4xl mx-auto flex flex-col gap-4'>
          {[
            {
              q: "What is included in the tour price?",
              a: "The tour price typically includes premium accommodation, professional guiding services, private air-conditioned vehicle transport, and entrance fees for designated sites mentioned in the itinerary. Please refer to the specific inclusions list for detailed items."
            },
            {
              q: "Is transport provided throughout the tour?",
              a: "Yes, private air-conditioned transport is provided from your arrival at the airport until your departure, driven by a dedicated, tourist-board approved English-speaking chauffeur-guide."
            },
            {
              q: "What should I bring for the tour?",
              a: "We recommend comfortable walking shoes, lightweight cotton clothing, sunscreen, sunglasses, insect repellent, a camera, and appropriate modest clothing (covering shoulders and knees) for visiting sacred temples."
            },
            {
              q: "When is the best time to visit Sri Lanka?",
              a: "Sri Lanka has two distinct monsoons, making it a year-round destination. The best time for the west and south coasts is from December to April, while the east coast is best from May to September."
            }
          ].map((item, idx) => (
            <div key={idx} className='border border-gray-100 rounded-2xl overflow-hidden shadow-sm bg-white transition-all duration-300'>
              <button 
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                className='w-full px-6 py-5 flex justify-between items-center text-left font-bold text-gray-800 hover:text-KITH-primary transition-colors gap-4'
                aria-expanded={openFaq === idx}
              >
                <span>{item.q}</span>
                <span className={`w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center text-xs transition-transform duration-300 ${openFaq === idx ? 'rotate-180 text-KITH-primary' : 'text-gray-400'}`}>
                  <i className='fi fi-br-angle-down mt-1'></i>
                </span>
              </button>
              {openFaq === idx && (
                <div className='px-6 pb-6 text-gray-600 text-sm md:text-base leading-relaxed border-t border-gray-50 pt-4'>
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Floating Action Buttons */}
      <div className='fixed bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-3 z-50 bg-white/20 backdrop-blur-2xl p-3 rounded-[2.5rem] border border-white/30 shadow-[0_20px_50px_rgba(0,0,0,0.2)] animate-fadeIn'>
          <button 
            onClick={() => setShowTierModal(true)}
            className='px-8 py-5 bg-KITH-primary text-white font-black uppercase tracking-widest text-[10px] rounded-[2rem] shadow-xl hover:bg-KITH-primary-dark transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3 whitespace-nowrap'
          >
              <i className='fi fi-br-check-circle text-xs'></i>
              Book Now
          </button>
          <a 
            href="mailto:info@kithtravels.com?subject=Meeting Request - KITH Travels"
            className='px-8 py-5 bg-white/90 text-gray-800 font-black uppercase tracking-widest text-[10px] rounded-[2rem] shadow-md hover:bg-white transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3 whitespace-nowrap'
          >
              <i className='fi fi-br-calendar text-xs'></i>
              Schedule Meeting
          </a>
      </div>

      {/* Experience Overlay Modal */}
      {selectedExperience && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn" onClick={(e) => e.target === e.currentTarget && setSelectedExperience(null)}>
            <div className="bg-white rounded-[2.5rem] p-8 md:p-10 max-w-lg w-full shadow-2xl relative overflow-hidden">
                <button onClick={() => setSelectedExperience(null)} className="absolute top-6 right-6 w-10 h-10 bg-white/50 backdrop-blur-md text-gray-800 rounded-full flex items-center justify-center hover:bg-white transition-colors z-10 shadow-sm border border-gray-100">
                    <i className="fi fi-br-cross text-xs"></i>
                </button>
                {selectedExperience.image && (
                    <div className="absolute top-0 left-0 right-0 h-48 bg-gray-200 -z-0">
                        <img src={(selectedExperience.image && typeof selectedExperience.image === 'object' ? (selectedExperience.image.h || selectedExperience.image.l) : selectedExperience.image)} alt={selectedExperience.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/80 to-transparent"></div>
                    </div>
                )}
                <div className={`relative z-10 ${selectedExperience.image ? 'mt-24' : ''}`}>
                    {!selectedExperience.image && (
                        <div className="w-16 h-16 bg-KITH-primary/10 rounded-full flex items-center justify-center mb-6">
                            <i className="fi fi-br-star text-KITH-primary text-2xl"></i>
                        </div>
                    )}
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">{selectedExperience.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-left whitespace-pre-line">
                        {selectedExperience.description || `Experience the beauty and culture of ${selectedExperience.title} as part of this tour.`}
                    </p>
                    <button onClick={() => setSelectedExperience(null)} className="mt-8 w-full py-4 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors">
                        Close
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Tier Selection Modal */}
      {showTierModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn" onClick={(e) => e.target === e.currentTarget && setShowTierModal(false)}>
            <div className="bg-white rounded-[2.5rem] p-8 md:p-10 max-w-2xl w-full shadow-2xl relative overflow-hidden flex flex-col gap-8">
                <button onClick={() => setShowTierModal(false)} className="absolute top-6 right-6 w-10 h-10 bg-gray-50 text-gray-800 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors z-10 shadow-sm border border-gray-100">
                    <i className="fi fi-br-cross text-xs"></i>
                </button>
                
                <div className="flex flex-col gap-2 pr-12">
                  <h3 className="text-3xl md:text-4xl font-black text-gray-800 tracking-tight">Select Tour Option</h3>
                  <p className="text-gray-500 text-sm md:text-base leading-relaxed">
                    Please note that the final rate may be subject to minor adjustments due to unforeseen circumstances. We kindly invite you to select the tier that best suits your travel preferences.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Tier */}
                  <div className="border border-gray-200 rounded-3xl p-6 md:p-8 flex flex-col gap-6 relative overflow-hidden bg-white hover:border-KITH-primary/30 hover:shadow-lg transition-all group">
                    <div className="flex flex-col gap-1">
                      <span className="text-KITH-primary font-bold uppercase tracking-widest text-xs">Standard</span>
                      <h4 className="text-2xl font-black text-gray-800">Basic Tour</h4>
                    </div>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <i className="fi fi-br-hotel text-KITH-primary"></i> May have 3-4 Star Hotels
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <i className="fi fi-br-car-side text-KITH-primary"></i> Standard Transport
                      </div>
                    </div>
                    <div className="mt-auto pt-6 border-t border-gray-100 flex flex-col gap-4">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-black text-gray-900">${tour.price}</span>
                        <span className="text-gray-400 text-sm">/person</span>
                      </div>
                      <Link 
                        to={`/book-tour/${tour.id}?tier=basic`}
                        className="w-full py-4 bg-gray-100 text-gray-800 font-bold rounded-2xl text-center hover:bg-gray-200 transition-colors"
                      >
                        Continue with Basic
                      </Link>
                    </div>
                  </div>

                  {/* Premium Tier */}
                  <div className="border-2 border-KITH-primary rounded-3xl p-6 md:p-8 flex flex-col gap-6 relative overflow-hidden bg-KITH-primary/5 hover:shadow-xl transition-all">
                    <div className="absolute top-0 right-0 bg-KITH-primary text-white text-[10px] font-black uppercase tracking-widest px-4 py-1 rounded-bl-xl">
                      Recommended
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-KITH-primary font-bold uppercase tracking-widest text-xs">Upgraded</span>
                      <h4 className="text-2xl font-black text-gray-800">Premium Tour</h4>
                    </div>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3 text-sm text-gray-800 font-medium">
                        <i className="fi fi-br-hotel text-KITH-primary"></i> Only 5 Star Hotels or Luxury Resorts
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-800 font-medium">
                        <i className="fi fi-br-car-side text-KITH-primary"></i> Luxury Transport
                      </div>
                    </div>
                    <div className="mt-auto pt-6 border-t border-KITH-primary/20 flex flex-col gap-4">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-black text-gray-900">${tour.premiumPrice || (tour.price * 1.5).toFixed(0)}</span>
                        <span className="text-gray-500 text-sm">/person</span>
                      </div>
                      <Link 
                        to={`/book-tour/${tour.id}?tier=premium`}
                        className="w-full py-4 bg-KITH-primary text-white font-bold rounded-2xl text-center hover:bg-KITH-primary-dark shadow-md transition-colors"
                      >
                        Continue with Premium
                      </Link>
                    </div>
                  </div>
                </div>

            </div>
        </div>
      )}
    </div>
  )
}

export default TourDetails
